import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, Phone, Mail, Globe, MapPin, Search, Plus, X, 
  Edit2, Trash2, Save, Filter, Thermometer, ThermometerSun, 
  ThermometerSnowflake, Calendar, RefreshCw, StickyNote, ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const businessTypes = [
  { value: 'tour_manager', label: 'Tour Manager' },
  { value: 'talent_buyer', label: 'Talent Buyer' },
  { value: 'amphitheater', label: 'Amphitheater' },
  { value: 'bar', label: 'Bar/Pub' },
  { value: 'venue', label: 'Venue' },
  { value: 'festival', label: 'Festival' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'other', label: 'Other' }
];

const leadStatuses = [
  { value: 'cold', label: 'Cold Lead', color: 'bg-blue-500', icon: ThermometerSnowflake },
  { value: 'hot', label: 'Hot Lead', color: 'bg-orange-500', icon: ThermometerSun },
  { value: 'no_lead', label: 'No Lead', color: 'bg-stone-500', icon: Thermometer },
  { value: 'booked', label: 'Booked', color: 'bg-green-500', icon: Calendar },
  { value: 'rebooked', label: 'Re-booked', color: 'bg-gold-500', icon: RefreshCw }
];

const ProspectingDashboard = () => {
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_type: {} });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});

  const [formData, setFormData] = useState({
    venue_name: '',
    business_type: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    lead_status: 'cold',
    notes: ''
  });

  useEffect(() => {
    fetchProspects();
    fetchStats();
  }, [filterType, filterStatus, searchTerm]);

  const fetchProspects = async () => {
    try {
      let url = `${BACKEND_URL}/api/prospects?`;
      if (filterType !== 'all') url += `business_type=${filterType}&`;
      if (filterStatus !== 'all') url += `lead_status=${filterStatus}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setProspects(data);
    } catch (error) {
      console.error('Failed to fetch prospects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/prospects/stats/summary`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingProspect 
        ? `${BACKEND_URL}/api/prospects/${editingProspect.id}`
        : `${BACKEND_URL}/api/prospects`;
      
      const method = editingProspect ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsAddModalOpen(false);
        setEditingProspect(null);
        resetForm();
        fetchProspects();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to save prospect:', error);
    }
  };

  const handleStatusChange = async (prospectId, newStatus) => {
    try {
      await fetch(`${BACKEND_URL}/api/prospects/${prospectId}/status?status=${newStatus}`, {
        method: 'PATCH'
      });
      fetchProspects();
      fetchStats();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleNotesUpdate = async (prospectId, notes) => {
    try {
      await fetch(`${BACKEND_URL}/api/prospects/${prospectId}/notes?notes=${encodeURIComponent(notes)}`, {
        method: 'PATCH'
      });
      fetchProspects();
    } catch (error) {
      console.error('Failed to update notes:', error);
    }
  };

  const handleDelete = async (prospectId) => {
    if (!window.confirm('Are you sure you want to delete this prospect?')) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/prospects/${prospectId}`, {
        method: 'DELETE'
      });
      fetchProspects();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete prospect:', error);
    }
  };

  const handleEdit = (prospect) => {
    setFormData({
      venue_name: prospect.venue_name,
      business_type: prospect.business_type,
      contact_name: prospect.contact_name,
      contact_email: prospect.contact_email,
      contact_phone: prospect.contact_phone,
      website: prospect.website,
      address: prospect.address,
      city: prospect.city,
      state: prospect.state,
      lead_status: prospect.lead_status,
      notes: prospect.notes
    });
    setEditingProspect(prospect);
    setIsAddModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      venue_name: '',
      business_type: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      website: '',
      address: '',
      city: '',
      state: '',
      lead_status: 'cold',
      notes: ''
    });
  };

  const getStatusInfo = (status) => {
    return leadStatuses.find(s => s.value === status) || leadStatuses[0];
  };

  const getTypeLabel = (type) => {
    return businessTypes.find(t => t.value === type)?.label || type;
  };

  return (
    <div className="min-h-screen bg-stone-950 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Prospect Manager</h1>
          <p className="text-stone-400">Track venues, leads, and booking opportunities</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-stone-900/50 border border-stone-800 rounded-xl p-4">
            <p className="text-stone-400 text-sm">Total</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          {leadStatuses.map(status => (
            <div key={status.value} className="bg-stone-900/50 border border-stone-800 rounded-xl p-4">
              <p className="text-stone-400 text-sm">{status.label}</p>
              <p className={`text-2xl font-bold ${status.color.replace('bg-', 'text-')}`}>
                {stats.by_status?.[status.value] || 0}
              </p>
            </div>
          ))}
        </div>

        {/* Filters and Search */}
        <div className="bg-stone-900/50 border border-stone-800 rounded-xl p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-grow relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <Input
                type="text"
                placeholder="Search venues, contacts, cities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-stone-900 border-stone-700 text-white"
              />
            </div>

            {/* Business Type Filter */}
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-48 bg-stone-900 border-stone-700 text-white">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Business Type" />
              </SelectTrigger>
              <SelectContent className="bg-stone-900 border-stone-700">
                <SelectItem value="all">All Types</SelectItem>
                {businessTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-48 bg-stone-900 border-stone-700 text-white">
                <Thermometer className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Lead Status" />
              </SelectTrigger>
              <SelectContent className="bg-stone-900 border-stone-700">
                <SelectItem value="all">All Statuses</SelectItem>
                {leadStatuses.map(status => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Add Button */}
            <Dialog open={isAddModalOpen} onOpenChange={(open) => {
              setIsAddModalOpen(open);
              if (!open) {
                setEditingProspect(null);
                resetForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-gold-500 hover:bg-gold-600 text-black">
                  <Plus className="w-4 h-4 mr-2" /> Add Prospect
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingProspect ? 'Edit Prospect' : 'Add New Prospect'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Venue/Company Name *</label>
                      <Input
                        name="venue_name"
                        value={formData.venue_name}
                        onChange={handleInputChange}
                        required
                        className="bg-stone-800 border-stone-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Business Type *</label>
                      <Select 
                        value={formData.business_type} 
                        onValueChange={(v) => handleSelectChange('business_type', v)}
                      >
                        <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-stone-900 border-stone-700">
                          {businessTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Contact Name</label>
                      <Input
                        name="contact_name"
                        value={formData.contact_name}
                        onChange={handleInputChange}
                        className="bg-stone-800 border-stone-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Lead Status</label>
                      <Select 
                        value={formData.lead_status} 
                        onValueChange={(v) => handleSelectChange('lead_status', v)}
                      >
                        <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-stone-900 border-stone-700">
                          {leadStatuses.map(status => (
                            <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Email</label>
                      <Input
                        type="email"
                        name="contact_email"
                        value={formData.contact_email}
                        onChange={handleInputChange}
                        className="bg-stone-800 border-stone-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Phone</label>
                      <Input
                        type="tel"
                        name="contact_phone"
                        value={formData.contact_phone}
                        onChange={handleInputChange}
                        className="bg-stone-800 border-stone-700 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-stone-300 text-sm mb-2">Website</label>
                    <Input
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://"
                      className="bg-stone-800 border-stone-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-stone-300 text-sm mb-2">Address</label>
                    <Input
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="bg-stone-800 border-stone-700 text-white"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">City</label>
                      <Input
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="bg-stone-800 border-stone-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">State</label>
                      <Input
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className="bg-stone-800 border-stone-700 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-stone-300 text-sm mb-2">Notes</label>
                    <Textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Add notes about this prospect..."
                      className="bg-stone-800 border-stone-700 text-white resize-none"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-grow bg-gold-500 hover:bg-gold-600 text-black">
                      <Save className="w-4 h-4 mr-2" />
                      {editingProspect ? 'Update Prospect' : 'Save Prospect'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setIsAddModalOpen(false);
                        setEditingProspect(null);
                        resetForm();
                      }}
                      className="border-stone-700 text-stone-300"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Prospects List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-stone-400">Loading prospects...</div>
          ) : prospects.length === 0 ? (
            <div className="text-center py-12 bg-stone-900/50 border border-stone-800 rounded-xl">
              <Building2 className="w-12 h-12 text-stone-600 mx-auto mb-4" />
              <p className="text-stone-400">No prospects found</p>
              <p className="text-stone-500 text-sm">Add your first prospect to get started</p>
            </div>
          ) : (
            prospects.map(prospect => {
              const statusInfo = getStatusInfo(prospect.lead_status);
              const StatusIcon = statusInfo.icon;
              
              return (
                <div 
                  key={prospect.id} 
                  className="bg-stone-900/50 border border-stone-800 rounded-xl p-4 sm:p-6 hover:border-stone-700 transition-colors"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    {/* Main Info */}
                    <div className="flex-grow">
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg ${statusInfo.color}/20 flex items-center justify-center flex-shrink-0`}>
                          <StatusIcon className={`w-5 h-5 ${statusInfo.color.replace('bg-', 'text-')}`} />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">{prospect.venue_name}</h3>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <span className="text-xs bg-stone-800 text-stone-300 px-2 py-1 rounded">
                              {getTypeLabel(prospect.business_type)}
                            </span>
                            <span className={`text-xs ${statusInfo.color}/20 ${statusInfo.color.replace('bg-', 'text-')} px-2 py-1 rounded`}>
                              {statusInfo.label}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Contact Details */}
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                        {prospect.contact_name && (
                          <div className="flex items-center gap-2 text-stone-400">
                            <Users className="w-4 h-4" />
                            <span>{prospect.contact_name}</span>
                          </div>
                        )}
                        {prospect.contact_phone && (
                          <a href={`tel:${prospect.contact_phone}`} className="flex items-center gap-2 text-stone-400 hover:text-gold-400">
                            <Phone className="w-4 h-4" />
                            <span>{prospect.contact_phone}</span>
                          </a>
                        )}
                        {prospect.contact_email && (
                          <a href={`mailto:${prospect.contact_email}`} className="flex items-center gap-2 text-stone-400 hover:text-gold-400">
                            <Mail className="w-4 h-4" />
                            <span className="truncate">{prospect.contact_email}</span>
                          </a>
                        )}
                        {prospect.website && (
                          <a href={prospect.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-stone-400 hover:text-gold-400">
                            <Globe className="w-4 h-4" />
                            <span className="truncate">Website</span>
                          </a>
                        )}
                        {(prospect.city || prospect.state) && (
                          <div className="flex items-center gap-2 text-stone-400">
                            <MapPin className="w-4 h-4" />
                            <span>{[prospect.city, prospect.state].filter(Boolean).join(', ')}</span>
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      {prospect.notes && (
                        <div className="mt-3">
                          <button
                            onClick={() => setExpandedNotes(prev => ({ ...prev, [prospect.id]: !prev[prospect.id] }))}
                            className="flex items-center gap-2 text-stone-500 hover:text-stone-300 text-sm"
                          >
                            <StickyNote className="w-4 h-4" />
                            <span>Notes</span>
                            <ChevronDown className={`w-4 h-4 transition-transform ${expandedNotes[prospect.id] ? 'rotate-180' : ''}`} />
                          </button>
                          {expandedNotes[prospect.id] && (
                            <div className="mt-2 p-3 bg-stone-800/50 rounded-lg text-stone-300 text-sm whitespace-pre-wrap">
                              {prospect.notes}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 lg:w-48 flex-shrink-0">
                      {/* Quick Status Buttons */}
                      <div className="flex flex-wrap gap-1">
                        {leadStatuses.map(status => (
                          <button
                            key={status.value}
                            onClick={() => handleStatusChange(prospect.id, status.value)}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              prospect.lead_status === status.value
                                ? `${status.color} text-white`
                                : `${status.color}/20 ${status.color.replace('bg-', 'text-')} hover:${status.color}/40`
                            }`}
                            title={status.label}
                          >
                            {status.label.split(' ')[0]}
                          </button>
                        ))}
                      </div>

                      {/* Edit/Delete */}
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(prospect)}
                          className="flex-grow border-stone-700 text-stone-300 hover:bg-stone-800"
                        >
                          <Edit2 className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(prospect.id)}
                          className="border-red-900 text-red-400 hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ProspectingDashboard;
